<template>
  <VaSidebar
    v-model="writableVisible"
    :minimized="minimized"
    :width="sidebarWidth"
    :minimized-width="mobile ? '0' : '56px'"
    :color="color"
    class="sidebar-layout rd-sidebar-chrome"
  >
    <div class="sidebar-content">
      <div v-for="group in navigationGroups" :key="group.title || 'primary-navigation'" class="nav-section">
        <div v-if="!minimized && group.title" class="nav-section-header">
          <span class="nav-section-title">{{ group.title }}</span>
          <button
            class="nav-section-toggle"
            type="button"
            :aria-label="`${isSectionCollapsed(group.title) ? 'Expand' : 'Collapse'} ${group.title} section`"
            :aria-expanded="!isSectionCollapsed(group.title)"
            :aria-controls="navSectionId(group.title)"
            @click="toggleSection(group.title)"
          >
            <svg class="sidebar-section-toggle-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path :d="sectionToggleIconPath(group.title)" />
            </svg>
          </button>
        </div>
        <VaAccordion v-show="!group.title || !isSectionCollapsed(group.title)" :id="group.title ? navSectionId(group.title) : undefined" v-model="value" multiple>
          <VaCollapse v-for="(route, index) in group.routes" :key="`${group.title}-${index}`">
            <template #header="{ value: isCollapsed }">
              <VaSidebarItem
                :to="route.children ? undefined : { name: route.name }"
                :active="routeHasActiveChild(route)"
                :active-color="activeColor"
                :text-color="textColor(route)"
                :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
                role="button"
                hover-opacity="0.10"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-4">
                    <svg
                      v-if="route.meta.icon"
                      class="sidebar-route-icon"
                      :class="{ 'sidebar-route-icon--active': routeHasActiveChild(route) }"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path :d="sidebarIconPath(route.meta.icon)" />
                    </svg>
                  <VaSidebarItemTitle v-if="!minimized" class="flex justify-between items-center leading-5 font-semibold">
                    {{ t(route.displayName) }}
                    <svg v-if="route.children" class="sidebar-route-arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path :d="routeArrowPath(isCollapsed)" />
                    </svg>
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </template>
            <template #body>
              <div v-for="(childRoute, index2) in route.children" :key="index2">
                <VaSidebarItem
                  :to="{ name: childRoute.name }"
                  :active="isActiveChildRoute(childRoute)"
                  :active-color="activeColor"
                  :text-color="textColor(childRoute)"
                  :aria-label="`Visit ${t(route.displayName)}`"
                  hover-opacity="0.10"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-11">
                    <VaSidebarItemTitle v-if="!minimized" class="leading-5 font-semibold">
                      {{ t(childRoute.displayName) }}
                    </VaSidebarItemTitle>
                  </VaSidebarItemContent>
                </VaSidebarItem>
              </div>
            </template>
          </VaCollapse>
        </VaAccordion>
      </div>
    </div>
    <div v-if="!minimized" class="sidebar-footer">
      <span class="version-text">v{{ version }}</span>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { usePermissions } from '../../composables/usePermissions'
import { fetchPanelProfile, isAdminAuthenticated } from '../../services/repodepot-api'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { APP_VERSION } from '../../version'

type NavigationGroup = {
  title: string
  routes: INavigationRoute[]
}

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    minimized: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const { filterNavigationRoutes } = usePermissions()
    const isAdminGroupUser = ref(false)

    const value = ref<boolean[]>([])
    const collapsedSections = ref<Record<string, boolean>>({})

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const filterAdminOnlyRoutes = (routes: INavigationRoute[]): INavigationRoute[] => {
      return routes
        .filter((item) => !item.requiresAdminGroup || isAdminGroupUser.value)
        .map((item) => ({
          ...item,
          ...(item.children ? { children: filterAdminOnlyRoutes(item.children as INavigationRoute[]) } : {}),
        }))
    }

    // Keep sidebar navigation stable across project context switches.
    const visibleRoutes = computed(() => {
      const permissionFiltered = isAdminGroupUser.value
        ? (navigationRoutes.routes as INavigationRoute[])
        : (filterNavigationRoutes(navigationRoutes.routes) as INavigationRoute[])
      return filterAdminOnlyRoutes(permissionFiltered)
    })

    const navigationGroups = computed<NavigationGroup[]>(() => {
      const routeByName = new Map(visibleRoutes.value.map((route) => [route.name, route]))
      const pickRoutes = (orderedNames: string[]) =>
        orderedNames.map((name) => routeByName.get(name)).filter((route): route is INavigationRoute => Boolean(route))

      return [
        {
          title: '',
          routes: pickRoutes(['dashboard']),
        },
        {
          title: 'System',
          routes: pickRoutes(['system-adc', 'system-permissions', 'system-users', 'operations-ai-first', 'operations-graph', 'security-guardian', 'backup', 'system-settings']),
        },
        {
          title: 'Support',
          routes: pickRoutes(['support-chat', 'support-tickets', 'support-desk', 'support-schedule']),
        },
        {
          title: 'RBAC',
          routes: pickRoutes(['users', 'projects', 'permissions', 'secrets']),
        },
      ].filter((group) => group.routes.length > 0)
    })

    onMounted(async () => {
      if (!isAdminAuthenticated()) {
        isAdminGroupUser.value = false
        return
      }

      try {
        const profile = await fetchPanelProfile()
        isAdminGroupUser.value = Boolean(profile?.is_system_admin)
      } catch {
        isAdminGroupUser.value = false
      }
    })

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name
      }

      return section.children.some(({ name }) => route.name === name)
    }

    const isSectionCollapsed = (title: string) => Boolean(title && collapsedSections.value[title])

    const toggleSection = (title: string) => {
      if (!title) {
        return
      }

      collapsedSections.value = {
        ...collapsedSections.value,
        [title]: !isSectionCollapsed(title),
      }
    }

    const navSectionId = (title: string) => `nav-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

    const sidebarIconPaths: Record<string, string> = {
      'vuestic-iconset-dashboard': 'M4 5h16v12H4z M8 21h8 M10 17v4 M14 17v4',
      admin_panel_settings: 'M12 3l7 3v5c0 4.2-2.8 7.8-7 10-4.2-2.2-7-5.8-7-10V6z M9 12l2 2 4-5',
      rule_book: 'M4 3h13a2 2 0 0 1 2 2v16H6a2 2 0 0 1-2-2V3z M4 17h15 M8 3v14 M11 7h5 M11 11h5',
      manage_accounts: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M2 21c.8-4 3.6-6 7-6 1.2 0 2.3.2 3.2.7 M17 14v7 M13.5 17.5h7',
      folder_shared: 'M3 6h7l2 2h9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 15a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M5.5 18c.6-1.5 1.8-2.3 3.5-2.3s2.9.8 3.5 2.3',
      groups: 'M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M16 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M2 20c.7-3.2 2.8-5 6-5s5.3 1.8 6 5 M10 20c.7-3.2 2.8-5 6-5 2.5 0 4.4 1.1 5.4 3.2',
      security: 'M12 3l7 3v5c0 4.2-2.8 7.8-7 10-4.2-2.2-7-5.8-7-10V6z M12 8v5 M12 16h.01',
      vpn_key: 'M8 14a4 4 0 1 1 3.5-6H22v4h-3v3h-3v-3h-4.5A4 4 0 0 1 8 14z M8 11h.01',
      psychology: 'M12 3a6 6 0 0 0-6 6v2.5L4 14v2h3v3h4v-4h2v4h4v-3h3v-2l-2-2.5V9a6 6 0 0 0-6-6z M9 9h.01 M15 9h.01 M10 13h4',
      account_tree: 'M6 4h6v5H6z M12 6h5v5 M17 11v3 M8 9v5 M5 14h6v6H5z M14 14h6v6h-6z',
      backup: 'M5 12a7 7 0 1 1 2 5 M5 12H2m3 0V9 M12 8v5l4 2',
      settings: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M12 2v3 M12 19v3 M4.9 4.9l2.1 2.1 M17 17l2.1 2.1 M2 12h3 M19 12h3 M4.9 19.1 7 17 M17 7l2.1-2.1',
      forum: 'M4 5h16v10H8l-4 4z M8 9h8 M8 12h6',
      confirmation_number: 'M4 6h16v4a2 2 0 0 0 0 4v4H4v-4a2 2 0 0 0 0-4z M9 8v8 M15 8v8',
      support_agent: 'M5 12a7 7 0 0 1 14 0v4 M5 12v4h4v-6H5z M19 12v4h-4v-6h4z M9 19c1 1 2 1.5 3 1.5s2-.5 3-1.5',
      schedule: 'M12 6v6l4 2 M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
    }

    const sidebarIconPath = (icon: string) => sidebarIconPaths[icon] || sidebarIconPaths.settings
    const sectionToggleIconPath = (title: string) => (isSectionCollapsed(title) ? 'M6 9l6 6 6-6' : 'M6 15l6-6 6 6')
    const routeArrowPath = (state: boolean) => (state ? 'M6 9l6 6 6-6' : 'M6 15l6-6 6 6')

    const setActiveExpand = () =>
      (value.value = visibleRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '200px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes: computed(() => ({
        ...navigationRoutes,
        routes: visibleRoutes.value,
      })),
      navigationGroups,
      routeHasActiveChild,
      isActiveChildRoute,
      isSectionCollapsed,
      toggleSection,
      navSectionId,
      sectionToggleIconPath,
      sidebarIconPath,
      routeArrowPath,
      t,
      textColor,
      version: APP_VERSION,
    }
  },
})
</script>

<style scoped>
.sidebar-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-layout:not(.va-sidebar--minimized) {
  width: 200px !important;
  min-width: 200px !important;
  max-width: 200px !important;
}

.sidebar-layout :deep(.va-sidebar__menu) {
  overflow-x: hidden;
}

.sidebar-layout:not(.va-sidebar--minimized) :deep(.va-sidebar__menu) {
  width: 200px !important;
  min-width: 200px !important;
  max-width: 200px !important;
}

.sidebar-layout :deep(.va-sidebar__item__content),
.sidebar-layout :deep(.va-sidebar-item-content) {
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.sidebar-layout :deep(.va-sidebar-item__title) {
  display: inline-flex;
  min-width: 0;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-route-icon,
.sidebar-route-arrow,
.sidebar-section-toggle-icon {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 1.25rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.sidebar-route-icon {
  color: var(--va-secondary);
}

.sidebar-route-icon--active {
  color: var(--va-primary);
}

.sidebar-route-arrow,
.sidebar-section-toggle-icon {
  width: 1rem;
  height: 1rem;
  flex-basis: 1rem;
}

.sidebar-layout :deep(.va-sidebar__item__content .va-icon),
.sidebar-layout :deep(.va-sidebar-item-content .va-icon) {
  flex-shrink: 0;
}

:global(.rd-sidebar-chrome .va-sidebar-item) {
  width: calc(100% - 16px);
}

:global(.rd-sidebar-chrome.va-sidebar--minimized .va-sidebar__item__content),
:global(.rd-sidebar-chrome.va-sidebar--minimized .va-sidebar-item-content) {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-section {
  margin-bottom: 0.25rem;
}

.nav-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.5rem 0.75rem 0.25rem 1rem;
}

.nav-section-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--va-text-secondary);
}

.nav-section-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  color: var(--va-text-secondary);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
}

.nav-section-toggle:hover {
  color: var(--va-text-primary);
  background: rgba(95, 209, 255, 0.08);
  border-color: rgba(95, 209, 255, 0.18);
}

.nav-section-toggle:focus-visible {
  outline: 2px solid rgba(95, 209, 255, 0.72);
  outline-offset: 2px;
}

.sidebar-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  margin-top: auto;
  border-top: 1px solid var(--va-background-border);
  flex-shrink: 0;
}

.version-text {
  font-size: 0.75rem;
  color: var(--va-textPrimary);
  opacity: 0.6;
}
</style>
